// UpsertFullMenuPage.tsx
import React, { useEffect, useState, useCallback, useRef, memo } from 'react';
import { v4 as uuidv4 } from "uuid";
import {
    Button, TextField, IconButton, Box, Typography, Paper, Checkbox, FormControlLabel,
    Container, Grid, Select, MenuItem, InputLabel, FormControl, OutlinedInput, Chip,
    Divider, Tooltip, CircularProgress, Card, CardContent, CardMedia, List, ListItem, ListItemText
} from '@mui/material';
import { Add, Delete, CheckCircle } from '@mui/icons-material';
import { useForm, useFieldArray, Controller, useWatch, FieldErrors } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDropzone } from 'react-dropzone';
import {
    FullMenuPageData, fullMenuPageSchema, BaseItem as BaseItemType, MenuItem as MenuItemType,
    Bundle as BundleType, ItemVariant, initialData, ComboVariant,
} from './upsertFullMenuHelpers';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { menuService } from '../../services/firebase/menuService';
import { ImageService } from '../../services/images/images';

// ---------- Utility Functions ----------
const flattenBaseItemVariants = (base: BaseItemType | undefined): ItemVariant[] => {
    if (!base?.variants) return [];
    return base.variants.flatMap(g => g.variants || []);
};

const flattenErrors = (errors: FieldErrors, pathPrefix = ''): string[] => {
    const messages: string[] = [];
    for (const key in errors) {
        if (!Object.prototype.hasOwnProperty.call(errors, key)) continue;
        const next = (errors as any)[key];
        const path = pathPrefix ? `${pathPrefix}.${key}` : key;
        if (next?.message) messages.push(`${path}: ${next.message}`);
        if (next?.types) {
            for (const t in next.types) messages.push(`${path}: ${next.types[t]}`);
        }
        if (next?.root === undefined && typeof next === 'object') {
            messages.push(...flattenErrors(next, path));
        }
    }
    return Array.from(new Set(messages));
};

// ---------- Price Conversion Utilities ----------
const formatPriceForDisplay = (cents: number): string => {
    if (isNaN(cents) || cents === null || cents === undefined) return '0.00';
    return (cents / 100).toFixed(2);
};

const parsePriceToInteger = (value: string): number => {
    const cleaned = value.replace(/[^\d.-]/g, '');
    const floatValue = parseFloat(cleaned);
    if (isNaN(floatValue)) return 0;
    return Math.round(floatValue * 100);
};

// ---------- Components ----------
const ErrorSummary: React.FC<{ errors: FieldErrors }> = ({ errors }) => {
    const messages = flattenErrors(errors);
    if (!messages.length) return null;
    return (
        <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid #d32f2f', backgroundColor: '#fff4f4' }}>
            <Typography variant="subtitle1" color="error">Validation errors</Typography>
            <List dense>
                {messages.map((m, i) => <ListItem key={i}><ListItemText primary={m} /></ListItem>)}
            </List>
        </Paper>
    );
};

// Custom Price Input Component
const PriceInput: React.FC<{
    value: number;
    onChange: (value: number) => void;
    label: string;
    size?: 'small' | 'medium';
    sx?: any;
    variant?: 'standard' | 'outlined' | 'filled';
}> = memo(({ value, onChange, label, size = 'small', sx, variant = 'outlined' }) => {
    const [displayValue, setDisplayValue] = useState(formatPriceForDisplay(value));

    useEffect(() => {
        setDisplayValue(formatPriceForDisplay(value));
    }, [value]);

    const handleBlur = useCallback(() => {
        const intValue = parsePriceToInteger(displayValue);
        onChange(intValue);
        setDisplayValue(formatPriceForDisplay(intValue));
    }, [displayValue, onChange]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setDisplayValue(e.target.value);
    }, []);

    return (
        <TextField
            label={label}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            type="number"
            size={size}
            sx={sx}
            variant={variant}
            InputProps={{
                startAdornment: <span style={{ marginRight: 4 }}>R</span>
            }}
        />
    );
});

const CustomTextInput: React.FC<{
    value: string;
    onChange: (value: string) => void;
    label: string;
    fullWidth?: boolean;
    type?: string;
    size?: 'small' | 'medium';
    margin?: 'dense' | 'normal' | 'none';
    multiline?: boolean;
    sx?: any;
    variant?: 'standard' | 'outlined' | 'filled';
}> = memo(({ value, onChange, label, size = 'small', sx, fullWidth, multiline, type, margin, variant = 'outlined' }) => {
    const [displayValue, setDisplayValue] = useState(value);

    useEffect(() => {
        setDisplayValue(value);
    }, [value]);

    const handleBlur = useCallback(() => {
        onChange(displayValue);
    }, [displayValue, onChange]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setDisplayValue(e.target.value);
    }, []);

    return (
        <TextField
            label={label}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            type={type}
            fullWidth={fullWidth}
            multiline={multiline}
            margin={margin}
            size={size}
            sx={sx}
            variant={variant}
        />
    );
});

type ImageUploadProps = {
    value?: string | null;
    onChange: (file: File | null) => void;
    label?: string;
    backgroundColor?: string;
};

const ImageUpload: React.FC<ImageUploadProps> = ({ value, onChange, label = 'Thumbnail', backgroundColor = '#fafafa' }) => {
    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles?.[0]?.type.startsWith('image/')) {
            onChange(acceptedFiles[0]);
        }
    }, [onChange]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop, accept: { "image/*": [] }, maxFiles: 1, maxSize: 5 * 1024 * 1024,
    });

    return (
        <Card elevation={0} sx={{ width: "100%", marginTop: 2, backgroundColor }}>
            <CardContent>
                <Typography variant="subtitle1" gutterBottom>{label}</Typography>
                <Box {...getRootProps()} sx={{
                    border: "2px dashed #cccccc", borderRadius: "5px", padding: 2, marginTop: 2,
                    textAlign: "center", backgroundColor, cursor: 'pointer'
                }}>
                    <input {...getInputProps()} />
                    <Typography variant="body2">
                        {isDragActive ? "Drop image to upload" : "Drag and drop your thumbnail here, or click to select files"}
                    </Typography>
                </Box>
                {value && (
                    <Box sx={{ marginTop: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CardMedia component="img" sx={{ width: 140, height: 100, objectFit: 'cover', borderRadius: 1 }}
                            image={value} alt="thumbnail" />
                        <Button size="small" onClick={() => onChange(null)}>Remove</Button>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

const OptionEditor: React.FC<{ control: any, basePath: string, title: string }> = ({ control, basePath, title }) => {
    const { fields, append, remove } = useFieldArray({ control, name: basePath, keyName: 'keyId' });

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">{title}</Typography>
            {fields.map((group, groupIndex) => (
                <Paper key={(group as any).id} sx={{ p: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        <Controller name={`${basePath}.${groupIndex}.name`} control={control}
                            render={({ field }) => <CustomTextInput onChange={field.onChange} value={field.value} label="Group Name" size="small" />} />
                        <Controller name={`${basePath}.${groupIndex}.description`} control={control}
                            render={({ field }) => <CustomTextInput onChange={field.onChange} value={field.value} label="Description" size="small" />} />
                        <IconButton onClick={() => remove(groupIndex)} size="small"><Delete /></IconButton>
                    </Box>
                    <NestedItemEditor control={control} basePath={`${basePath}.${groupIndex}.${title.toLowerCase().includes('option') ? 'options' : 'variants'}`} />
                </Paper>
            ))}
            <Button size="small" startIcon={<Add />} onClick={() => append({
                id: uuidv4(), name: title.includes('Option') ? 'Extras' : 'Default Group', description: '',
                [title.toLowerCase().includes('option') ? 'options' : 'variants']: []
            })}>
                Add {title.slice(0, -1)} Group
            </Button>
        </Box>
    );
};

const NestedItemEditor: React.FC<{ control: any, basePath: string }> = ({ control, basePath }) => {
    const { fields, append, remove } = useFieldArray({ control, name: basePath, keyName: 'keyId' });
    const isOption = basePath.includes('options');

    return (
        <Box sx={{ mt: 1, ml: 2 }}>
            {fields.map((item, itemIndex) => (
                <Box key={(item as any).id} sx={{ display: 'flex', flexDirection: "column", gap: 1, alignItems: 'start', mb: 1 }}>
                    <Controller name={`${basePath}.${itemIndex}.isSoldOut`} control={control}
                        render={({ field }) => (
                            <FormControlLabel control={<Checkbox checked={!!field.value}
                                onChange={(e) => field.onChange(e.target.checked)} />}
                                label="Is Sold Out" />
                        )} />
                    <Box key={(item as any).id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        <Controller name={`${basePath}.${itemIndex}.name`} control={control}
                            render={({ field }) => <CustomTextInput onChange={field.onChange} value={field.value} label={`${isOption ? 'Option' : 'Variant'} Name`} size="small" />} />
                        <Controller name={`${basePath}.${itemIndex}.price`} control={control}
                            render={({ field }) =>
                                <PriceInput
                                    value={field.value || 0}
                                    onChange={field.onChange}
                                    label={isOption ? "Price Adj." : "Price"}
                                    sx={{ width: '100px' }}
                                    size='small'
                                />} />
                        {!isOption && (
                            <Controller name={`${basePath}.${itemIndex}.comboPrice`} control={control}
                                render={({ field }) =>
                                    <PriceInput
                                        value={field.value || 0}
                                        onChange={field.onChange}
                                        label="Combo Price"
                                        size="small"
                                        sx={{ width: '120px' }} />} />
                        )}
                        <IconButton onClick={() => remove(itemIndex)} size="small"><Delete /></IconButton>
                    </Box>
                </Box>
            ))}
            <Button size="small" startIcon={<Add />} onClick={() => append({
                id: uuidv4(), name: '', price: 0, ...(isOption ? {} : { comboPrice: 0 })
            })}>
                Add {isOption ? 'Option' : 'Variant'}
            </Button>
        </Box>
    );
};

const BaseItems: React.FC<{
    control: any, usedBaseItemIds: Set<string>, fileMap: Map<string, File>,
    updateFileMap: (key: string, file: File | null) => void
}> = ({ control, usedBaseItemIds, fileMap, updateFileMap }) => {
    const { fields, append, remove } = useFieldArray({ control, name: "baseItems", keyName: "keyId" });

    return (
        <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>Item Inventory</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Define all the individual items you sell. These are the building blocks for your menu.
            </Typography>

            {fields.map((item: any, index) => {
                const isUsed = usedBaseItemIds.has(item.id);
                const fileKey = `baseItem.${item.id}.imageFile`;
                const previewUrl = fileMap.get(fileKey) ? URL.createObjectURL(fileMap.get(fileKey)!) : item.imageUrl;

                return (
                    <Paper key={item.keyId} sx={{ p: 2, mb: 2, border: '1px solid #ddd', position: 'relative', opacity: isUsed ? 1 : 0.95 }}>
                        {isUsed && <Chip icon={<CheckCircle />} label="On Menu" color="success" size="small"
                            sx={{ position: 'absolute', top: 8, right: 8 }} />}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Controller name={`baseItems.${index}.name`} control={control}
                                render={({ field }) => <CustomTextInput onChange={field.onChange} value={field.value} label="Base Item Name" variant="standard" />} />
                            <Tooltip title={isUsed ? "This item is used on the menu and cannot be deleted." : "Delete Item"}>
                                <span>
                                    <IconButton onClick={() => remove(index)} disabled={isUsed}><Delete /></IconButton>
                                </span>
                            </Tooltip>
                        </Box>

                        <Controller name={`baseItems.${index}.price`} control={control}
                            render={({ field }) =>
                                <PriceInput
                                    value={field.value || 0}
                                    onChange={field.onChange}
                                    label="Base Item Price"
                                    variant="standard" />} />

                        <Controller name={`baseItems.${index}.includedInMetrics`} control={control}
                            render={({ field }) => (
                                <FormControlLabel control={<Checkbox checked={!!field.value}
                                    onChange={(e) => field.onChange(e.target.checked)} />}
                                    label="Is included in metrics" />
                            )} />
                        <Controller name={`baseItems.${index}.isSoldOut`} control={control}
                            render={({ field }) => (
                                <FormControlLabel control={<Checkbox checked={!!field.value}
                                    onChange={(e) => field.onChange(e.target.checked)} />}
                                    label="Is Sold Out" />
                            )} />

                        <Controller name={`baseItems.${index}.imageUrl`} control={control}
                            render={({ field }) => (
                                <ImageUpload value={previewUrl} onChange={(file) => {
                                    updateFileMap(fileKey, file);
                                    field.onChange(file ? URL.createObjectURL(file) : undefined);
                                }} label="Base Item Thumbnail" />
                            )} />

                        <OptionEditor control={control} basePath={`baseItems.${index}.variants`} title="Variant Groups" />
                        <Divider sx={{ my: 2 }} />
                        <OptionEditor control={control} basePath={`baseItems.${index}.options`} title="Ingredient Option Groups" />
                    </Paper>
                );
            })}

            <Button startIcon={<Add />} onClick={() => append({
                id: uuidv4(), name: '', price: 0, includedInMetrics: false, imageUrl: undefined,
                variants: [{ id: uuidv4(), name: 'Default Group', description: '', variants: [{ id: uuidv4(), name: 'Standard', price: 0, comboPrice: 0 }] }],
                options: []
            })}>Add New Base Item</Button>
        </Paper>
    );
};

const ComboVariants: React.FC<{ control: any, categoryIndex: number, itemIndex: number, baseItems: BaseItemType[], categoryId: string, menuItemId: string, fileMap: Map<string, File>, updateFileMap: (key: string, file: File | null) => void }> =
    ({ control, categoryIndex, itemIndex, baseItems, categoryId, menuItemId, fileMap, updateFileMap }) => {
        const { fields, append, remove } = useFieldArray({ control, name: `menu.categories.${categoryIndex}.items.${itemIndex}.comboVariants`, keyName: 'keyId' });

        return (
            <Box sx={{ mt: 2, ml: 2, pl: 2, borderLeft: '2px solid #ccc' }}>
                <Typography variant="subtitle1">Combo Variants</Typography>
                {fields.map((variant: any, comboVariantIndex) => {
                    const fileKey = `comboVariant.${categoryId}.${menuItemId}.${variant.id}.imageFile`;
                    const previewUrl = fileMap.get(fileKey) ? URL.createObjectURL(fileMap.get(fileKey)!) : variant.imageUrl;

                    return (
                        <Paper key={variant.id} sx={{ p: 2, my: 1, backgroundColor: '#f0f0f0' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2">Combo Variant #{comboVariantIndex + 1}</Typography>
                                <IconButton onClick={() => remove(comboVariantIndex)} size="small"><Delete /></IconButton>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <Controller name={`menu.categories.${categoryIndex}.items.${itemIndex}.comboVariants.${comboVariantIndex}.name`}
                                    control={control} render={({ field }) => <CustomTextInput onChange={field.onChange} value={field.value} label="Variant Name" size="small" />} />
                                <Controller name={`menu.categories.${categoryIndex}.items.${itemIndex}.comboVariants.${comboVariantIndex}.price`}
                                    control={control} render={({ field }) =>
                                        <PriceInput
                                            value={field.value || 0}
                                            onChange={field.onChange}
                                            label="Price"
                                            size="small" />} />
                            </Box>

                            <Controller name={`menu.categories.${categoryIndex}.items.${itemIndex}.comboVariants.${comboVariantIndex}.imageUrl`}
                                control={control} render={({ field }) => (
                                    <ImageUpload backgroundColor='#f0f0f0' value={previewUrl} onChange={(file) => {
                                        updateFileMap(fileKey, file);
                                        field.onChange(file ? URL.createObjectURL(file) : undefined);
                                    }} label="Menu Combo Thumbnail" />
                                )} />

                            <FormControl fullWidth margin="dense">
                                <InputLabel>Included Items</InputLabel>
                                <Controller name={`menu.categories.${categoryIndex}.items.${itemIndex}.comboVariants.${comboVariantIndex}.itemVariantIds`}
                                    control={control} render={({ field }) => (
                                        <Select {...field} multiple input={<OutlinedInput label="Included Items" />}
                                            renderValue={(selected: string[]) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((id) => {
                                                        const item = baseItems.find(v => v.id === id);
                                                        return <Chip key={id} label={item?.name || 'Unknown'} />;
                                                    })}
                                                </Box>
                                            )}>
                                            {baseItems.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                        </Select>
                                    )} />
                            </FormControl>
                        </Paper>
                    );
                })}
                <Button size="small" startIcon={<Add />} onClick={() => append({ id: uuidv4(), name: '', price: 0, itemVariantIds: [] })}>
                    Add Combo Variant
                </Button>
            </Box>
        );
    };

const MenuItems: React.FC<{
    control: any, categoryIndex: number, baseItems: BaseItemType[], usedMenuItemIds: Set<string>,
    categoryId: string, fileMap: Map<string, File>, updateFileMap: (key: string, file: File | null) => void
}> = ({ control, categoryIndex, baseItems, usedMenuItemIds, categoryId, fileMap, updateFileMap }) => {
    const { fields, append, remove } = useFieldArray({ control, name: `menu.categories.${categoryIndex}.items`, keyName: 'keyId' });
    const watchFieldArray = useWatch({ control, name: `menu.categories.${categoryIndex}.items` });
    const controlledFields = fields.map((field, index) => ({ ...field, ...watchFieldArray[index] }));

    return (
        <Box sx={{ ml: 2, pl: 2, borderLeft: '2px solid #eee' }}>
            {controlledFields.map((item, itemIndex) => {
                const isUsed = usedMenuItemIds.has(item.id);
                const fileKey = `menuItem.${categoryId}.${item.id}.imageFile`;
                const previewUrl = fileMap.get(fileKey) ? URL.createObjectURL(fileMap.get(fileKey)!) : item.imageUrl;

                return (
                    <Paper key={(item as any).id} sx={{ p: 2, my: 2, backgroundColor: '#fafafa' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">Menu Item</Typography>
                            <Tooltip title={isUsed ? "This item is used in a bundle and cannot be deleted." : "Delete Menu Item"}>
                                <span>
                                    <IconButton onClick={() => remove(itemIndex)} disabled={isUsed}><Delete /></IconButton>
                                </span>
                            </Tooltip>
                        </Box>

                        <FormControl fullWidth margin="dense">
                            <InputLabel>Select Base Item</InputLabel>
                            <Controller name={`menu.categories.${categoryIndex}.items.${itemIndex}.baseItemId`} control={control}
                                render={({ field }) => (
                                    <Select {...field} label="Select Base Item">
                                        {baseItems.map(baseItem => <MenuItem key={baseItem.id} value={baseItem.id}>{baseItem.name}</MenuItem>)}
                                    </Select>
                                )} />
                        </FormControl>

                        <Controller name={`menu.categories.${categoryIndex}.items.${itemIndex}.isCombo`} control={control}
                            render={({ field }) => (
                                <FormControlLabel control={<Checkbox checked={!!field.value}
                                    onChange={(e) => field.onChange(e.target.checked)} />}
                                    label="Is a Combo Meal" />
                            )} />

                        <Controller name={`menu.categories.${categoryIndex}.items.${itemIndex}.imageUrl`} control={control}
                            render={({ field }) => (
                                <ImageUpload value={previewUrl} onChange={(file) => {
                                    updateFileMap(fileKey, file);
                                    field.onChange(file ? URL.createObjectURL(file) : undefined);
                                }} label="Menu Item Thumbnail" />
                            )} />

                        {!item.isCombo ? (
                            <>
                                <Controller name={`menu.categories.${categoryIndex}.items.${itemIndex}.isOnSale`} control={control}
                                    render={({ field }) => (
                                        <FormControlLabel control={<Checkbox checked={!!field.value}
                                            onChange={(e) => field.onChange(e.target.checked)} />}
                                            label="Item is on Sale" />
                                    )} />
                                {item.isOnSale &&
                                    <Controller name={`menu.categories.${categoryIndex}.items.${itemIndex}.salePrice`} control={control}
                                        render={({ field }) =>
                                            <PriceInput
                                                value={field.value || 0}
                                                onChange={field.onChange}
                                                label="Sale Price"
                                                size="small" />} />
                                }
                            </>
                        ) : (
                            <ComboVariants control={control} categoryIndex={categoryIndex} itemIndex={itemIndex}
                                baseItems={baseItems} categoryId={categoryId} menuItemId={item.id}
                                fileMap={fileMap} updateFileMap={updateFileMap} />
                        )}
                    </Paper>
                );
            })}
            <Button size="small" startIcon={<Add />} onClick={() => append({
                id: uuidv4(), baseItemId: '', isOnSale: false, isCombo: false, comboVariants: []
            })}>Add Menu Item</Button>
        </Box>
    );
};

const BundleBuilder: React.FC<{
    control: any, allMenuItems: (MenuItemType & { baseItem?: BaseItemType })[], setValue: Function,
    fileMap: Map<string, File>, updateFileMap: (key: string, file: File | null) => void
}> = ({ control, allMenuItems, setValue, fileMap, updateFileMap }) => {
    const { fields: bundles, append: appendBundle, remove: removeBundle } = useFieldArray({ control, name: "menu.bundles", keyName: 'keyId' });

    return (
        <Box>
            <Typography variant="h6" gutterBottom>Deals</Typography>
            {bundles.map((bundle: any, bundleIndex) => {
                const fileKey = `bundle.${bundle.id}.imageFile`;
                const previewUrl = fileMap.get(fileKey) ? URL.createObjectURL(fileMap.get(fileKey)!) : bundle.imageUrl;

                return (
                    <Paper key={bundle.id} sx={{ p: 2, mb: 2, border: '1px solid #ddd' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Controller name={`menu.bundles.${bundleIndex}.name`} control={control}
                                render={({ field }) => <CustomTextInput onChange={field.onChange} value={field.value} label="Bundle Name" variant="standard" />} />
                            <IconButton onClick={() => removeBundle(bundleIndex)}><Delete /></IconButton>
                        </Box>

                        <Controller name={`menu.bundles.${bundleIndex}.description`} control={control}
                            render={({ field }) => <CustomTextInput onChange={field.onChange} value={field.value} label="Description" fullWidth multiline margin="dense" />} />

                        <Controller name={`menu.bundles.${bundleIndex}.imageUrl`} control={control}
                            render={({ field }) => (
                                <ImageUpload value={previewUrl} onChange={(file) => {
                                    updateFileMap(fileKey, file);
                                    field.onChange(file ? URL.createObjectURL(file) : undefined);
                                }} label="Bundle Thumbnail" />
                            )} />

                        <Divider sx={{ my: 2 }} />
                        <BundleItemsEditor control={control} bundleIndex={bundleIndex} allMenuItems={allMenuItems} setValue={setValue} />
                    </Paper>
                );
            })}

            <Button startIcon={<Add />} onClick={() => appendBundle({ id: uuidv4(), name: '', description: '', items: [] })}>
                Add New Bundle
            </Button>
        </Box>
    );
};

const BundleItemsEditor: React.FC<{
    control: any, bundleIndex: number, allMenuItems: (MenuItemType & { baseItem?: BaseItemType })[], setValue: Function
}> = ({ control, bundleIndex, allMenuItems, setValue }) => {
    const { fields, append, remove } = useFieldArray({ control, name: `menu.bundles.${bundleIndex}.items`, keyName: 'keyId' });
    const bundleItems = useWatch({ control, name: `menu.bundles.${bundleIndex}.items` }) as Array<{ menuItemId?: string }>;

    const handleMenuItemChange = (itemIndex: number, newMenuItemId: string) => {
        const selectedMenuItem = allMenuItems.find(mi => mi.id === newMenuItemId);
        const variants = selectedMenuItem?.isCombo
            ? selectedMenuItem.comboVariants
            : [];
        const priceOverrides = variants?.map(v => ({ id: uuidv4(), variantId: v.id, price: (v as any).price || 0 })) || [];

        setValue(`menu.bundles.${bundleIndex}.items.${itemIndex}.menuItemId`, newMenuItemId);
        setValue(`menu.bundles.${bundleIndex}.items.${itemIndex}.variantPriceOverrides`, priceOverrides);
    };

    return (
        <Box>
            {fields.map((item: any, index) => {
                const selectedMenuItemId = bundleItems?.[index]?.menuItemId;
                const selectedMenuItem = allMenuItems.find(mi => mi.id === selectedMenuItemId);
                const variants = selectedMenuItem?.isCombo
                    ? selectedMenuItem.comboVariants
                    : undefined;

                return (
                    <Paper key={item.id} sx={{ p: 2, my: 1, backgroundColor: '#f9f9f9' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1">Bundle Item #{index + 1}</Typography>
                            <IconButton onClick={() => remove(index)}><Delete /></IconButton>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <FormControl fullWidth margin="dense" sx={{ flex: 2 }}>
                                <InputLabel>Menu Item</InputLabel>
                                <Controller name={`menu.bundles.${bundleIndex}.items.${index}.menuItemId`} control={control}
                                    render={({ field }) => (
                                        <Select {...field} onChange={(e) => handleMenuItemChange(index, e.target.value)} label="Menu Item">
                                            {allMenuItems.map(mi => <MenuItem key={mi.id} value={mi.id}>{mi.baseItem?.name}</MenuItem>)}
                                        </Select>
                                    )} />
                            </FormControl>
                            <Controller name={`menu.bundles.${bundleIndex}.items.${index}.quantity`} control={control}
                                render={({ field }) => <CustomTextInput onChange={field.onChange} value={field.value} label="Quantity" type="number" sx={{ flex: 1 }} />} />
                        </Box>

                        {variants && (
                            <Box sx={{ ml: 4, mt: 1 }}>
                                {variants.map((variant: ItemVariant | ComboVariant, variantIndex: number) => (
                                    <Box key={variant.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography sx={{ flex: 1 }}>{variant.name}</Typography>
                                        <Controller name={`menu.bundles.${bundleIndex}.items.${index}.variantPriceOverrides.${variantIndex}.price`}
                                            control={control} render={({ field }) =>
                                                <PriceInput
                                                    value={field.value || 0}
                                                    onChange={field.onChange}
                                                    label="Bundle Price"
                                                    size="small" />} />
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {!variants && (
                            <Box sx={{ ml: 4, mt: 1 }}>
                                <Box key={"empty-bundle-" + bundleIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Controller name={`menu.bundles.${bundleIndex}.items.${index}.noVariantPriceOverride`}
                                        control={control} render={({ field }) =>
                                            <PriceInput
                                                value={field.value || 0}
                                                onChange={field.onChange}
                                                label="Bundle Price"
                                                size="small"
                                                sx={{ width: "100%" }} />} />
                                </Box>
                            </Box>
                        )}
                    </Paper>
                );
            })}
            <Button startIcon={<Add />} onClick={() => append({
                id: uuidv4(), menuItemId: '', quantity: 1, variantPriceOverrides: []
            })}>Add Item to Bundle</Button>
        </Box>
    );
};

const MenuBuilder: React.FC<{
    control: any, baseItems: BaseItemType[], allMenuItems: (MenuItemType & { baseItem?: BaseItemType })[],
    usedMenuItemIds: Set<string>, setValue: Function, fileMap: Map<string, File>,
    updateFileMap: (key: string, file: File | null) => void
}> = ({ control, baseItems, allMenuItems, usedMenuItemIds, setValue, fileMap, updateFileMap }) => {
    const { fields: categories, append: appendCategory, remove: removeCategory } = useFieldArray({ control, name: "menu.categories", keyName: 'keyId' });
    const watchedCategories = useWatch({ control, name: "menu.categories" }) as Array<{ items: any[], id?: string }>;

    return (
        <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>Menu Builder</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Construct your customer-facing menu using items from your inventory.
            </Typography>

            {categories.map((category: any, categoryIndex) => {
                const isUsed = (watchedCategories[categoryIndex]?.items?.length ?? 0) > 0;
                const categoryId = watchedCategories[categoryIndex]?.id || category.id || `categoryIdx.${categoryIndex}`;

                return (
                    <Paper key={category.id} sx={{ p: 2, mb: 2, border: '1px solid #ddd' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Controller name={`menu.categories.${categoryIndex}.name`} control={control}
                                render={({ field }) => <CustomTextInput onChange={field.onChange} value={field.value} label="Category Name" variant="standard" />} />
                            <Tooltip title={isUsed ? "Category has food items and cannot be deleted." : "Delete Category"}>
                                <span>
                                    <IconButton onClick={() => removeCategory(categoryIndex)} disabled={isUsed}><Delete /></IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                        <MenuItems control={control} categoryIndex={categoryIndex} baseItems={baseItems}
                            usedMenuItemIds={usedMenuItemIds} categoryId={categoryId}
                            fileMap={fileMap} updateFileMap={updateFileMap} />
                    </Paper>
                );
            })}

            <Button startIcon={<Add />} onClick={() => appendCategory({ id: uuidv4(), name: '', items: [] })}>
                Add Menu Category
            </Button>
            <Divider sx={{ my: 4 }} />
            <BundleBuilder control={control} allMenuItems={allMenuItems} setValue={setValue}
                fileMap={fileMap} updateFileMap={updateFileMap} />
        </Paper>
    );
};

// ---------- Main Component ----------
const UpsertFullMenuPage: React.FC = () => {
    const { restaurantId } = useParams<{ restaurantId: string; menuId: string }>();
    const [loading, setLoading] = useState<boolean>(true);
    const fileMapRef = useRef<Map<string, File>>(new Map());
    const [fileMapVersion, setFileMapVersion] = useState<number>(0);

    const { control, handleSubmit, watch, setValue, formState } = useForm<FullMenuPageData>({
        resolver: yupResolver(fullMenuPageSchema),
        defaultValues: initialData || { baseItems: [], menu: { categories: [], bundles: [] } },
    });

    const updateFileMap = useCallback((key: string, file: File | null) => {
        if (file) {
            fileMapRef.current.set(key, file);
        } else {
            fileMapRef.current.delete(key);
        }
        setFileMapVersion(v => v + 1);
    }, []);

    const applyUrlForKey = (data: FullMenuPageData, key: string, url: string) => {
        console.log("data:", data, "key:", key, "url:", url)
        const parts = key.split('.');
        if (parts[0] === 'baseItem' && parts.length >= 3) {
            const baseId = parts[1];
            const bi = data.baseItems.find(b => b.id === baseId);
            if (bi) bi.imageUrl = url;
        } else if (parts[0] === 'menuItem' && parts.length >= 4) {
            const categoryId = parts[1];
            const menuItemId = parts[2];
            const cat = data.menu.categories.find(c => c.id === categoryId);
            console.log("categoryId", categoryId, "menuItemId", menuItemId)
            if (cat) {
                const mi = (cat.items || []).find(i => i.id === menuItemId);
                console.log("menu item", mi)
                if (mi) mi.imageUrl = url;
            }
        } else if (parts[0] === 'comboVariant' && parts.length >= 5) {
            const categoryId = parts[1];
            const menuItemId = parts[2];
            const comboVariantId = parts[3];
            const cat = data.menu.categories.find(c => c.id === categoryId);
            if (cat) {
                const mi = (cat.items || []).find(i => i.id === menuItemId);
                if (mi && mi.comboVariants) {
                    const cv = mi.comboVariants.find((v: any) => v.id === comboVariantId);
                    if (cv) cv.imageUrl = url;
                }
            }
        } else if (parts[0] === 'bundle' && parts.length >= 3) {
            const bundleId = parts[1];
            const b = data.menu.bundles.find(bu => bu.id === bundleId);
            if (b) b.imageUrl = url;
            console.log("bundle", b, "url", url)
        }
    };

    const onSubmit = async (data: FullMenuPageData) => {
        if (!restaurantId) {
            toast.error("Invalid Restaurant");
            return;
        }

        try {
            setLoading(true);

            // Handle file uploads
            const entries = Array.from(fileMapRef.current.entries());
            console.log("entries", entries)
            if (entries.length > 0) {
                // Upload unique files
                const uploadedUrls = await ImageService.uploadImagesToStorage(entries.map((value: [string, File]) => value[1]));
                console.log("uploadedUrls", uploadedUrls)

                // Apply URLs to all keys that shared the same file
                const count = uploadedUrls.length;
                for (let i = 0; i < count; i++) {
                    const url = uploadedUrls[i];
                    applyUrlForKey(data, entries[i][0], url);
                }

                // Clean up file map
                fileMapRef.current.clear();
            }

            await menuService.upsertFullMenu(restaurantId, data);
            await fetchData()
            toast.success("Menu saved successfully!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to save menu.");
        } finally {
            setLoading(false);
        }
    };

    const onInvalid = (errs: FieldErrors<FullMenuPageData>) => {
        console.error("Form validation errors:", errs);
        const messages = flattenErrors(errs);
        if (messages.length) {
            toast.error(messages.slice(0, 3).join(' • '));
        } else {
            toast.error("Please fix the errors before saving.");
        }
    };

    useEffect(() => {

        fetchData();
    }, [restaurantId, setValue]);

    const fetchData = async () => {
        if (!restaurantId) {
            toast.error("Invalid Restaurant");
            return;
        }
        try {
            setLoading(true);
            const data = await menuService.fetchFullMenuCached(restaurantId);
            setValue('baseItems', data.baseItems);
            setValue('menu.categories', data.menu.categories);
            setValue('menu.bundles', data.menu.bundles);
        } catch (err) {
            toast.error("Failed to fetch menu data.");
        } finally {
            setLoading(false);
        }
    };

    // Computed values
    const baseItems = watch('baseItems') || [];
    const categories = watch('menu.categories') || [];
    const bundles = watch('menu.bundles') || [];

    const allMenuItems = categories.flatMap(c =>
        (c.items || []).map(i => ({
            ...i,
            baseItem: baseItems.find(b => b.id === i.baseItemId)
        }))
    );

    const usedBaseItemIds = new Set<string>(
        categories.flatMap(category =>
            (category.items || []).flatMap((item: any) => {
                if (!item.isCombo) return [item.baseItemId];
                return (item.comboVariants || []).flatMap((variant: any) =>
                    (variant.itemVariantIds || []).map((variantId: string) =>
                        baseItems.find((b: BaseItemType) =>
                            flattenBaseItemVariants(b).some(v => v.id === variantId)
                        )?.id
                    ).filter((id: any): id is string => Boolean(id))
                );
            })
        )
    );

    const usedMenuItemIds = new Set(
        bundles.flatMap((b: any) => (b.items || []).map((i: any) => i.menuItemId))
    );

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
                <Typography variant="h4" gutterBottom>Manage Menu</Typography>
                <ErrorSummary errors={formState.errors} />

                <Grid container spacing={4}>
                    <Grid item xs={12} md={5}>
                        <BaseItems control={control} usedBaseItemIds={usedBaseItemIds}
                            fileMap={fileMapRef.current} updateFileMap={updateFileMap} />
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <MenuBuilder control={control} baseItems={baseItems} allMenuItems={allMenuItems}
                            usedMenuItemIds={usedMenuItemIds} setValue={setValue}
                            fileMap={fileMapRef.current} updateFileMap={updateFileMap} />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="submit" variant="contained" size="large" disabled={loading}>
                        Save Full Menu
                    </Button>
                </Box>
            </form>
        </Container>
    );
};

export default UpsertFullMenuPage;